import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';

export default function SpeedProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [form, setForm] = useState({ name: '', download_speed: '', upload_speed: '', download_mbps: '', upload_mbps: '', olt_line_profile_id: '', olt_service_profile_id: '', vlan_id: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      post: (url, body) => fetch(url, { method: 'POST', headers, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
      del: (url) => fetch(url, { method: 'DELETE', headers }).then(r => r.json()),
    };
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await api.get(`/api/noc/speed-profiles${params}`);
      setProfiles(data.items || []);
    } catch (e) { setError('Failed to load speed profiles'); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const openAdd = () => {
    setEditProfile(null);
    setForm({ name: '', download_speed: '', upload_speed: '', download_mbps: '', upload_mbps: '', olt_line_profile_id: '', olt_service_profile_id: '', vlan_id: '', description: '' });
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditProfile(p);
    setForm({ name: p.name, download_speed: p.download_speed || '', upload_speed: p.upload_speed || '', download_mbps: p.download_mbps || '', upload_mbps: p.upload_mbps || '', olt_line_profile_id: p.olt_line_profile_id || '', olt_service_profile_id: p.olt_service_profile_id || '', vlan_id: p.vlan_id || '', description: p.description || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Profile name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, download_mbps: form.download_mbps ? parseInt(form.download_mbps) : null, upload_mbps: form.upload_mbps ? parseInt(form.upload_mbps) : null, olt_line_profile_id: form.olt_line_profile_id ? parseInt(form.olt_line_profile_id) : null, olt_service_profile_id: form.olt_service_profile_id ? parseInt(form.olt_service_profile_id) : null, vlan_id: form.vlan_id ? parseInt(form.vlan_id) : null };
      if (editProfile) { await api.put(`/api/noc/speed-profiles/${editProfile.id}`, payload); }
      else { await api.post('/api/noc/speed-profiles', payload); }
      setShowForm(false); loadProfiles();
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this speed profile?')) return;
    try { await api.del(`/api/noc/speed-profiles/${id}`); loadProfiles(); }
    catch (e) { setError('Delete failed'); }
  };

  const SpeedBar = ({ mbps }) => {
    const max = 1000;
    const pct = Math.min((mbps || 0) / max * 100, 100);
    const color = mbps >= 500 ? '#0052CC' : mbps >= 100 ? '#36B37E' : mbps >= 50 ? '#FF991F' : '#6B778C';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 80, height: 6, background: '#DFE1E6', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 12, color, fontWeight: 600 }}>{mbps || 0}M</span>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#172B4D' }}>Speed Profiles</h2>
          <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 13 }}>Manage bandwidth plans and OLT line/service profile mappings</p>
        </div>
        <button onClick={openAdd} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          + Add Speed Profile
        </button>
      </div>

      {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#BF2600', fontSize: 13 }}>{error}</div>}

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search speed profiles..." onKeyDown={e => e.key === 'Enter' && loadProfiles()}
          style={{ flex: 1, border: '1px solid #DFE1E6', borderRadius: 5, padding: '7px 12px', fontSize: 14, outline: 'none' }} />
        <button onClick={loadProfiles} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '7px 16px', cursor: 'pointer', fontSize: 14 }}>Search</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#F4F5F7' }}>
              {['#', 'Profile Name', 'Download', 'Upload', 'DL Mbps', 'UL Mbps', 'Line Profile ID', 'Service Profile ID', 'VLAN', 'Actions'].map(h => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: '#5E6C84', borderBottom: '1px solid #DFE1E6', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>Loading...</td></tr>
            ) : profiles.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>No speed profiles found.</td></tr>
            ) : profiles.map((p, i) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #F4F5F7' }} onMouseEnter={e => e.currentTarget.style.background='#F8F9FA'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{i + 1}</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#172B4D' }}>{p.name}</td>
                <td style={{ padding: '10px 14px' }}><span style={{ background: '#DEEBFF', color: '#0747A6', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>↓ {p.download_speed || '—'}</span></td>
                <td style={{ padding: '10px 14px' }}><span style={{ background: '#E3FCEF', color: '#006644', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>↑ {p.upload_speed || '—'}</span></td>
                <td style={{ padding: '10px 14px' }}><SpeedBar mbps={p.download_mbps} /></td>
                <td style={{ padding: '10px 14px' }}><SpeedBar mbps={p.upload_mbps} /></td>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{p.olt_line_profile_id || '—'}</td>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{p.olt_service_profile_id || '—'}</td>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{p.vlan_id || '—'}</td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(p)} style={{ background: '#E3FCEF', color: '#006644', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', marginRight: 6, fontSize: 12 }}>Edit</button>
                  <button onClick={() => handleDelete(p.id)} style={{ background: '#FFEBE6', color: '#BF2600', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#172B4D' }}>{editProfile ? 'Edit Speed Profile' : 'Add Speed Profile'}</h3>
            {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 5, padding: '8px 12px', marginBottom: 14, color: '#BF2600', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Profile Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Premium_100M"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {[['download_speed','Download Speed','e.g. 100M'],['upload_speed','Upload Speed','e.g. 100M'],['download_mbps','Download Mbps','e.g. 100'],['upload_mbps','Upload Mbps','e.g. 100'],['olt_line_profile_id','OLT Line Profile ID','e.g. 1'],['olt_service_profile_id','OLT Service Profile ID','e.g. 1'],['vlan_id','VLAN ID','e.g. 100']].map(([k,l,ph]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>{l}</label>
                  <input value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} placeholder={ph}
                    style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Optional notes..."
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ background: '#F4F5F7', color: '#344563', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
